// src/components/Header.jsx

import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  useScrollTrigger,
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MenuOutlined,
  CloseOutlined,
  DashboardOutlined,
  LoginOutlined,
  PersonAddOutlined,
  LogoutOutlined,
  AutoAwesomeOutlined,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

// Animated Logo Component
function AnimatedLogo() {
  return (
    <motion.div
      initial={{ scale: 0, rotate: -180 }}
      animate={{ scale: 1, rotate: 0 }}
      transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
        }}
      >
        <motion.div
          animate={{ 
            rotate: [0, 360],
            scale: [1, 1.2, 1],
          }}
          transition={{ 
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        >
          <AutoAwesomeOutlined 
            sx={{ 
              fontSize: 32,
              color: 'var(--primary-blue)',
              filter: 'drop-shadow(0 0 10px rgba(59, 130, 246, 0.3))',
            }} 
          />
        </motion.div>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 900,
            fontFamily: 'Space Grotesk, sans-serif',
            background: 'var(--gradient-accent)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '-0.02em',
          }}
        >
          CardsPay
        </Typography>
      </Box>
    </motion.div>
  );
}

// Navigation Link Component
function NavLink({ to, children, icon, onClick }) {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <motion.div
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      <Button
        component={Link}
        to={to}
        onClick={onClick}
        startIcon={icon}
        sx={{
          color: isActive ? 'var(--primary-blue)' : 'var(--text-primary)',
          fontWeight: isActive ? 700 : 500,
          position: 'relative',
          textTransform: 'none',
          fontSize: '16px',
          px: 2,
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -2,
            left: '50%',
            transform: 'translateX(-50%)',
            width: isActive ? '100%' : '0%',
            height: '2px',
            background: 'linear-gradient(90deg, var(--primary-blue), var(--primary-sky))',
            transition: 'width 0.3s ease',
          },
          '&:hover::after': {
            width: '100%',
          },
          '&:hover': {
            background: 'transparent',
            color: 'var(--primary-blue)',
          },
        }}
      >
        {children}
      </Button>
    </motion.div>
  );
}

// Mobile Menu Item Component
function MobileMenuItem({ to, icon, title, onClick }) {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <motion.div
      initial={{ x: -50, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: -50, opacity: 0 }}
      whileHover={{ x: 10 }}
      whileTap={{ scale: 0.95 }}
    >
      <Box
        component={Link}
        to={to}
        onClick={onClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          color: isActive ? 'var(--primary-blue)' : 'var(--text-primary)',
          textDecoration: 'none',
          borderRadius: 2,
          transition: 'all 0.3s ease',
          background: isActive ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
          '&:hover': {
            background: 'rgba(59, 130, 246, 0.1)',
            color: 'var(--primary-blue)',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: 2,
            background: isActive 
              ? 'linear-gradient(45deg, var(--primary-blue), var(--primary-sky))' 
              : 'var(--bg-secondary)',
            transition: 'all 0.3s ease',
          }}
        >
          {React.cloneElement(icon, { 
            sx: { 
              color: isActive ? 'white' : 'var(--text-secondary)',
              fontSize: 20,
            } 
          })}
        </Box>
        <Typography
          sx={{
            fontWeight: isActive ? 700 : 500,
            fontSize: '16px',
          }}
        >
          {title}
        </Typography>
      </Box>
    </motion.div>
  );
}

export default function Header() {
  const { currentUser, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    setMobileOpen(false);
  };

  const navItems = currentUser ? [
    { to: '/dashboard', title: 'Dashboard', icon: <DashboardOutlined /> },
  ] : [];

  const authItems = currentUser
    ? [{ title: 'Logout', icon: <LogoutOutlined />, onClick: handleLogout }]
    : [
        { to: '/login', title: 'Login', icon: <LoginOutlined /> },
        { to: '/register', title: 'Register', icon: <PersonAddOutlined /> },
      ];

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: scrolled || trigger
            ? 'var(--glass-dark)' 
            : 'transparent',
          backdropFilter: scrolled || trigger ? 'blur(20px)' : 'none',
          borderBottom: scrolled || trigger ? '1px solid var(--border-primary)' : 'none',
          transition: 'all 0.3s ease',
          transform: trigger ? 'translateY(-100%)' : 'translateY(0)',
        }}
      >
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            py: 1,
          }}
        >
          <Link to={currentUser ? "/dashboard" : "/login"} style={{ textDecoration: 'none' }}>
            <AnimatedLogo />
          </Link>

          {/* Desktop Navigation */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: 1,
            }}
          >
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} icon={item.icon}>
                {item.title}
              </NavLink>
            ))}
            
            <Box sx={{ width: '1px', height: 24, bgcolor: 'var(--border-primary)', mx: 1 }} />
            
            {authItems.map((item) => (
              item.to ? (
                <NavLink key={item.to} to={item.to} icon={item.icon}>
                  {item.title}
                </NavLink>
              ) : (
                <motion.div key={item.title} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    onClick={item.onClick}
                    startIcon={item.icon}
                    sx={{
                      color: 'var(--text-primary)',
                      fontWeight: 500,
                      textTransform: 'none',
                      fontSize: '16px',
                      px: 2,
                      '&:hover': {
                        background: 'transparent',
                        color: 'var(--accent-error)',
                      },
                    }}
                  >
                    {item.title}
                  </Button>
                </motion.div>
              )
            ))}

            {currentUser && (
              <Box
                sx={{
                  ml: 2,
                  px: 2,
                  py: 0.5,
                  borderRadius: 20,
                  background: 'linear-gradient(45deg, var(--primary-blue), var(--primary-sky))',
                  boxShadow: '0 4px 20px rgba(59, 130, 246, 0.3)',
                }}
              >
                <Typography variant="caption" sx={{ color: 'white', fontWeight: 600 }}>
                  {currentUser.email}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Mobile Menu Button */}
          <IconButton
            onClick={() => setMobileOpen(!mobileOpen)}
            sx={{
              display: { xs: 'flex', md: 'none' },
              color: 'var(--text-primary)',
              zIndex: 1301,
            }}
          >
            <motion.div
              animate={{ rotate: mobileOpen ? 90 : 0 }}
              transition={{ duration: 0.3 }}
            >
              {mobileOpen ? <CloseOutlined /> : <MenuOutlined />}
            </motion.div>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: '100%',
            maxWidth: 320,
            background: 'var(--bg-card)',
            borderLeft: '1px solid var(--border-primary)',
            pt: 10,
            px: 2,
          },
        }}
      >
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {navItems.map((item, index) => (
                  <motion.div
                    key={item.to}
                    initial={{ x: -50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <MobileMenuItem
                      to={item.to}
                      icon={item.icon}
                      title={item.title}
                      onClick={() => setMobileOpen(false)}
                    />
                  </motion.div>
                ))}

                <Box sx={{ my: 2, height: '1px', bgcolor: 'var(--border-primary)' }} />

                {authItems.map((item, index) => (
                  <motion.div
                    key={item.title}
                    initial={{ x: -50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: (navItems.length + index) * 0.1 }}
                  >
                    {item.to ? (
                      <MobileMenuItem
                        to={item.to}
                        icon={item.icon}
                        title={item.title}
                        onClick={() => setMobileOpen(false)}
                      />
                    ) : (
                      <Box
                        onClick={item.onClick}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          p: 2,
                          color: 'var(--accent-error)',
                          cursor: 'pointer',
                          borderRadius: 2,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            background: 'rgba(239, 68, 68, 0.1)',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            background: 'rgba(239, 68, 68, 0.1)',
                          }}
                        >
                          {React.cloneElement(item.icon, { 
                            sx: { color: 'var(--accent-error)', fontSize: 20 } 
                          })}
                        </Box>
                        <Typography sx={{ fontWeight: 600, fontSize: '16px' }}>
                          {item.title}
                        </Typography>
                      </Box>
                    )}
                  </motion.div>
                ))}
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Drawer>
    </>
  );
}
